import { QueryClient } from "@tanstack/react-query";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSessionUser } from "~/api/session_queries";
import { useCurrentSite } from '~/api/sites';
import { sharedQueryKeys } from "~/api/utils/sharedQueryKeys";
import { useIsAtOrWiderThan } from "~/shared/utils/useBreakpoint";
import { syncAxiosCsrfTokenWithCookie } from "~/utils/csrf-token-utils";
import useSubscribeToEmitterEvent from "~/utils/events/use-subscribe-to-event";
import useShowSignUpFlow from "../session/useShowSignUpFlow";
import { Empty } from "../types/utility";
import { emitCsrfTokenChange, signInCloseMessage } from "~/utils/csrf-token-utils";
import { useWindowEventListener } from "~/utils/use-event-listener";
import { useHasInteracted } from "../utils/useHasInteracted";
import useUrlAttributionTracking from "../utils/useUrlAttributionTracking";

// client for react-query
export const reactQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
    },
  },
});

const handleSignInMessage = (event: Event) => {
  console.log('listenForCsrfTokenChange', { event });
  if (event instanceof MessageEvent && event.data === signInCloseMessage) {
    // token has changed after signin
    emitCsrfTokenChange()
  }
}

type CommonAppState = ReturnType<typeof useInitCommonAppState>
export const CommonAppContext = React.createContext({} as CommonAppState);

type CommonAppStateInitConfig = {
  sideBarInitiallyOpen?: boolean
}

export const useInitCommonAppState = ({
  options = {}
}: {
  options?: CommonAppStateInitConfig
} = {}) => {
  const isMd = useIsAtOrWiderThan('md')
  const isRoot = window.location.pathname === '/';
  const [csrfToken, setCsrfToken] = useState<string | null>(null)
  const [sideBarOpen, setSideBarOpen] = React.useState(isRoot ? false : isMd);
  const { data: sessionUserData, isLoading: isLoadingSessionUser, isFetched: hasFetchedSessionUser } = useSessionUser();
  const sessionUser = sessionUserData || null
  const userNotSignedIn = hasFetchedSessionUser && !sessionUser
  const navigate = useNavigate();
  const { showSignInDialog, setShowSignInDialog, initialSignInMode } = useShowSignUpFlow({ autoOpenDialogOnToken: true });
  const { data: currentSite } = useCurrentSite();
  const { locale: localeParam } = useParams()
  const locale = localeParam ? localeParam : 'en'
  const hasInteracted = useHasInteracted()

  useUrlAttributionTracking({ sessionUser })

  // RELATIVE url to return to after signing in, ie /organizations/1/etc
  // Must be RELATIVE url for navigate() to work correctly
  const [pendingSignInUrl, setRelativePendingSignInUrl] = useState<string>()

  useEffect(() => {
    // change html lang attribute 
    document.documentElement.lang = locale
  }, [locale])

  // set this in the app base, or per page
  // if you do both, you'll get a flicker
  const [layout, setLayout] = useState<string | Empty>()

  useEffect(() => {
    if (!hasFetchedSessionUser) return;

    if (sessionUser) { // sign in -ish
      // close sign up dialog after sign in
      setShowSignInDialog(false)

      // invalidate all queries to show fresh data for signed in user
      reactQueryClient.invalidateQueries()

      // use <RequireSessionUser> in component to force session user

    }
  }, [sessionUser?.id])

  useSubscribeToEmitterEvent<string | null>('csrfTokenChanged', setCsrfToken)
  //useSubscribeToEmitterEvent('signOutSuccessful', onSignOut)

  useWindowEventListener('message', handleSignInMessage)

  useEffect(() => {
    reactQueryClient.invalidateQueries({ queryKey: [sharedQueryKeys.session.user] })
    syncAxiosCsrfTokenWithCookie({ csrfTokenInState: csrfToken })
  }, [csrfToken])

  useEffect(() => {
    if (pendingSignInUrl && sessionUser) {
      setRelativePendingSignInUrl(undefined)
      navigate(pendingSignInUrl)
    }
  }, [pendingSignInUrl, sessionUser])


  return {
    sideBarOpen,
    setSideBarOpen,
    sessionUser,
    isLoadingSessionUser,
    hasFetchedSessionUser,
    userNotSignedIn,
    csrfToken,
    setCsrfToken,
    pendingSignInUrl,
    setRelativePendingSignInUrl,
    layout,
    setLayout,
    showSignInDialog,
    setShowSignInDialog,
    initialSignInMode,
    currentSite,
    locale,
    hasInteracted,
  }
};


export const useCommonAppContext = () => (
  useContext(CommonAppContext)
)